import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  AutoComplete,
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Steps,
} from 'antd'
import {
  HomeOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import {
  indiaLocationAPI,
  sellerAuthAPI,
  type IndiaLocationOption,
  type IndiaStateOption,
  type SellerRegistrationPayload,
} from '../utils/sellerApi'

const { Password, TextArea } = Input

interface SellerRegistrationValues extends SellerRegistrationPayload {
  terms: boolean
}

const businessTypes = [
  'Individual',
  'Sole Proprietorship',
  'Partnership',
  'LLP',
  'Private Limited',
  'Other',
]

const stepTitles = [
  'Business profile',
  'Compliance and address',
  'Payout details',
  'Security and review',
]

const phonePattern = /^[0-9]{10,15}$/
const gstPattern = /^[0-9A-Z]{8,20}$/
const postalPattern = /^[0-9A-Za-z-]{4,12}$/
const accountPattern = /^[0-9]{8,20}$/
const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/
const strongPasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/

const resolveApiErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError(error)) {
    const apiMessage = (error.response?.data as { message?: string } | undefined)?.message
    if (apiMessage) {
      return apiMessage
    }

    if (!error.response) {
      return 'Unable to reach registration API. Please verify backend server, CORS, and VITE_API_URL.'
    }
  }

  return fallback
}

const isFormValidationError = (error: unknown): error is { errorFields: unknown[] } => {
  return Boolean(error && typeof error === 'object' && 'errorFields' in error)
}

const stepFields: Array<Array<keyof SellerRegistrationValues>> = [
  [
    'business_name',
    'business_type',
    'owner_name',
    'owner_number',
    'business_email',
    'business_phone',
    'business_purpose',
  ],
  [
    'business_address',
    'business_address1',
    'business_city',
    'business_state',
    'business_postal_code',
    'business_country',
    'gst_number',
    'documents',
  ],
  ['bank_account_number', 'bank_ifsc', 'bank_name'],
  ['password', 'password_confirmation', 'terms'],
]

const registrationFields = stepFields.flat()

const buildCityLabel = (city: IndiaLocationOption): string => {
  const parts = [city.name]

  if (city.district && city.district !== city.name) {
    parts.push(city.district)
  }

  if (city.postal_code) {
    parts.push(city.postal_code)
  }

  return parts.join(' • ')
}

const fillSellerAddressFromLocation = async (
  latitude: number,
  longitude: number,
  form: ReturnType<typeof Form.useForm<SellerRegistrationValues>>[0],
  setCityOptions: (next: IndiaLocationOption[]) => void,
) => {
  try {
    const response = await indiaLocationAPI.reverse({ latitude, longitude })
    const location = response.data.data

    if (!location) {
      return false
    }

    const cityName = location.city?.trim() || ''
    const stateName = location.state?.trim() || ''
    const postalCode = location.postal_code?.trim() || ''

    form.setFieldsValue({
      business_address: location.address || form.getFieldValue('business_address'),
      business_address1: location.address1 || form.getFieldValue('business_address1'),
      business_address2: location.address2 || form.getFieldValue('business_address2'),
      business_city: cityName || form.getFieldValue('business_city'),
      business_state: stateName || form.getFieldValue('business_state'),
      business_postal_code: postalCode || form.getFieldValue('business_postal_code'),
      business_country: location.country || form.getFieldValue('business_country') || 'India',
    })

    if (stateName) {
      setCityOptions([
        {
          name: cityName || stateName,
          state: stateName,
          district: location.district || null,
          postal_code: postalCode || null,
        },
      ])
    }

    return true
  } catch {
    return false
  }
}

const mapPayload = (values: SellerRegistrationValues): SellerRegistrationPayload => {
  const documents = (Array.isArray(values.documents) ? values.documents : [])
    .map((document) => String(document ?? '').trim())
    .filter((document) => document.length > 0)

  const safe = (v?: string) => String(v ?? '').trim()

  return {
    business_name: safe(values.business_name),
    business_type: values.business_type,
    owner_name: safe(values.owner_name),
    owner_number: safe(values.owner_number),
    business_email: safe(values.business_email).toLowerCase(),
    business_phone: safe(values.business_phone),
    password: values.password,
    password_confirmation: values.password_confirmation,
    business_address: safe(values.business_address),
    business_address1: safe(values.business_address1),
    business_address2: safe(values.business_address2) || undefined,
    business_city: safe(values.business_city),
    business_state: safe(values.business_state),
    business_postal_code: safe(values.business_postal_code),
    business_country: safe(values.business_country),
    gst_number: safe(values.gst_number).toUpperCase(),
    business_purpose: safe(values.business_purpose),
    documents,
    bank_account_number: safe(values.bank_account_number) || undefined,
    bank_ifsc: safe(values.bank_ifsc).toUpperCase() || undefined,
    bank_name: safe(values.bank_name) || undefined,
  }
}

const SellerRegisterPage = () => {
  const [form] = Form.useForm<SellerRegistrationValues>()
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [stateOptions, setStateOptions] = useState<IndiaStateOption[]>([])
  const [cityOptions, setCityOptions] = useState<IndiaLocationOption[]>([])
  const [statesLoading, setStatesLoading] = useState(false)
  const [citiesLoading, setCitiesLoading] = useState(false)
  const suppressCityClearRef = useRef(false)
  const previousStateRef = useRef('')
  const navigate = useNavigate()
  const { message } = App.useApp()
  const selectedState = Form.useWatch('business_state', form) as string | undefined

  const quote = '"Trust is the first product every great seller ships."'

  const currentStepFields = useMemo(() => stepFields[currentStep], [currentStep])

  useEffect(() => {
    let active = true

    const loadStates = async () => {
      setStatesLoading(true)

      try {
        const response = await indiaLocationAPI.states()

        if (active) {
          setStateOptions(response.data.data)
        }
      } catch {
        if (active) {
          message.warning('Unable to load Indian states from OpenStreetMap right now. You can still type manually.')
        }
      } finally {
        if (active) {
          setStatesLoading(false)
        }
      }
    }

    void loadStates()

    return () => {
      active = false
    }
  }, [message])

  useEffect(() => {
    const state = (selectedState ?? '').trim()
    const previous = previousStateRef.current

    previousStateRef.current = state

    // Only refresh city suggestions when state changes.
    // Do NOT clear the selected city value.
    if (previous && previous !== state) {
      setCityOptions([])
    }
  }, [selectedState])
  // Load cities on user interaction (dropdown open). This prevents repeated
  // API calls on every state change and preserves any user-typed city value.
  const loadCitiesForState = async (state?: string) => {
    const s = String(state ?? '').trim()
    if (!s) {
      message.warning('Select a state first to load city suggestions.')
      return
    }

    setCitiesLoading(true)
    try {
      const response = await indiaLocationAPI.cities({ state: s, limit: 50 })
      setCityOptions(response.data.data)
    } catch {
      message.warning('Unable to load cities for the selected state. You can still type the city manually.')
    } finally {
      setCitiesLoading(false)
    }
  }

  const goNext = async () => {
    try {
      await form.validateFields(currentStepFields)
      setCurrentStep((previous) => Math.min(previous + 1, stepTitles.length - 1))
    } catch {
      // Validation errors are shown inline by antd form items.
    }
  }

  const goPrevious = () => {
    setCurrentStep((previous) => Math.max(previous - 1, 0))
  }

  const onFinish = async () => {
    setLoading(true)
    try {
      const values = (await form.validateFields(registrationFields)) as SellerRegistrationValues
      const payload = mapPayload(values)
      await sellerAuthAPI.register(payload)

      message.success('Seller registration submitted successfully.')
      navigate('/seller/login', {
        state: {
          message:
            'Your seller request is waiting for admin approval. Once approved, use the password you created during registration to sign in.',
        },
      })
    } catch (error: unknown) {
      if (isFormValidationError(error)) {
        return
      }

      message.error(resolveApiErrorMessage(error, 'Registration failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="seller-auth-page">
      <div className="seller-auth-shell seller-auth-shell--register">
        <div className="seller-auth-copy seller-auth-copy--register">
          <p className="seller-auth-eyebrow">Seller onboarding</p>
          <h1>Build a trusted brand and scale it with precision.</h1>
          <p>
            Complete this guided onboarding flow to register your business profile, verify compliance,
            configure payout preferences, and secure your seller workspace.
          </p>

          <blockquote className="seller-auth-quote">
            {quote}
            <cite>Trendify Marketplace Standard</cite>
          </blockquote>

          <ul className="seller-auth-points">
            <li>Stage-wise registration with instant validation at every step.</li>
            <li>Compliance-first onboarding to protect marketplace trust.</li>
            <li>Secure account setup before dashboard and plan activation.</li>
          </ul>
        </div>

        <div className="seller-auth-panel seller-auth-panel--register">
          <Spin spinning={loading}>
            <Card
              className="seller-auth-card seller-auth-card--register"
              title="Become a Trendify Seller"
              styles={{ header: { textAlign: 'center', fontSize: 24, fontWeight: 700 } }}
            >
              <Alert
                title="Submit your business details for admin review"
                description="After approval, sign in with the password you created during registration and activate a plan to unlock the seller dashboard."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Steps
                current={currentStep}
                size="small"
                className="seller-register-steps"
                items={stepTitles.map((title) => ({ title }))}
              />
              <Form
                form={form}
                preserve={true}
                layout="vertical"
                onFinish={onFinish}
                size="large"
                scrollToFirstError
                style={{ marginTop: 20 }}
                initialValues={{
                  business_country: 'India',
                }}
              >

                <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                  <>
                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="business_name"
                          label="Business Name"
                          rules={[{ required: true, message: 'Business name is required' }]}
                        >
                          <Input prefix={<ShopOutlined />} placeholder="Trendify Fashion House" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="business_type"
                          label="Business Type"
                          rules={[{ required: true, message: 'Business type is required' }]}
                        >
                          <Select
                            placeholder="Select business type"
                            options={businessTypes.map((type) => ({ label: type, value: type }))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="owner_name"
                          label="Owner Name"
                          rules={[{ required: true, message: 'Owner name is required' }]}
                        >
                          <Input prefix={<UserOutlined />} placeholder="John Doe" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="owner_number"
                          label="Owner Contact Number"
                          rules={[
                            { required: true, message: 'Owner contact number is required' },
                            { pattern: phonePattern, message: 'Enter a valid 10 to 15 digit phone number' },
                          ]}
                        >
                          <Input prefix={<PhoneOutlined />} placeholder="9876543210" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="business_email"
                          label="Business Email"
                          rules={[
                            { required: true, message: 'Business email is required' },
                            { type: 'email', message: 'Please enter a valid business email' },
                          ]}
                        >
                          <Input prefix={<MailOutlined />} placeholder="business@company.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="business_phone"
                          label="Business Phone"
                          rules={[
                            { required: true, message: 'Business phone is required' },
                            { pattern: phonePattern, message: 'Enter a valid 10 to 15 digit phone number' },
                          ]}
                        >
                          <Input prefix={<PhoneOutlined />} placeholder="9001002003" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="business_purpose"
                      label="Business Purpose"
                      rules={[
                        { required: true, message: 'Business purpose is required' },
                        { min: 20, message: 'Write at least 20 characters for better review context' },
                      ]}
                    >
                      <TextArea rows={4} placeholder="Tell Trendify what you plan to sell and how you want to use the seller dashboard" />
                    </Form.Item>
                  </>
                </div>
                <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                                    <>
                    <Form.Item
                      name="business_address"
                      label="Registered Address"
                      rules={[{ required: true, message: 'Registered address is required' }]}
                    >
                      <TextArea rows={3} placeholder="Enter the full registered business address" />
                    </Form.Item>

                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="business_address1"
                          label="Address Line 1"
                          rules={[{ required: true, message: 'Address line 1 is required' }]}
                        >
                          <Input prefix={<HomeOutlined />} placeholder="Street or Area" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="business_address2" label="Address Line 2 (Optional)">
                          <Input prefix={<HomeOutlined />} placeholder="Landmark or Building" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      title="Location suggestions are sourced from OpenStreetMap through the backend lookup API."
                      description="Pick a valid state first, then the city and postal code suggestions will be loaded from cached OpenStreetMap data."
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                      <Button
                        onClick={async () => {
                          if (!navigator.geolocation) {
                            message.warning('Geolocation is not supported by your browser.')
                            return
                          }

                          message.loading({ content: 'Getting your current location...', key: 'seller-current-location' })

                          navigator.geolocation.getCurrentPosition(
                            async (position) => {
                              const latitude = Number(position.coords.latitude.toFixed(7))
                              const longitude = Number(position.coords.longitude.toFixed(7))

                              suppressCityClearRef.current = true
                              const success = await fillSellerAddressFromLocation(
                                latitude,
                                longitude,
                                form,
                                setCityOptions,
                              )
                              // allow the selectedState effect to run once without clearing
                              setTimeout(() => {
                                suppressCityClearRef.current = false
                              }, 250)

                              if (success) {
                                message.success({ content: 'Current location loaded into your address.', key: 'seller-current-location' })
                              } else {
                                message.warning({ content: 'Location found, but the address could not be resolved. Please edit manually.', key: 'seller-current-location' })
                              }
                            },
                            () => {
                              message.error({ content: 'Unable to access your current location.', key: 'seller-current-location' })
                            },
                            { enableHighAccuracy: true, timeout: 7000, maximumAge: 300000 },
                          )
                        }}
                      >
                        Use current location
                      </Button>
                    </div>

                    <Row gutter={18}>
                      <Col xs={24} md={6}>
                        <Form.Item name="business_city" label="City" rules={[{ required: true, message: 'City is required' }]}>
                          <AutoComplete
                            allowClear
                            options={cityOptions.map((city) => ({
                              label: buildCityLabel(city),
                              value: city.name,
                            }))}
                            filterOption={(input, option) => String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                            notFoundContent={citiesLoading ? <Spin size="small" /> : null}
                            onFocus={() => {
                              if (!cityOptions.length) void loadCitiesForState(selectedState)
                            }}
                            onOpenChange={(open) => {
                              if (open && !cityOptions.length) void loadCitiesForState(selectedState)
                            }}
                            placeholder="City"
                          >
                            <Input placeholder="City" />
                          </AutoComplete>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item name="business_state" label="State" rules={[{ required: true, message: 'State is required' }]}>
                          <AutoComplete
                            allowClear
                            options={stateOptions.map((state) => ({
                              label: `${state.name} (${state.code})`,
                              value: state.name,
                            }))}
                            filterOption={(input, option) => String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                            notFoundContent={statesLoading ? <Spin size="small" /> : null}
                            placeholder="State"
                          >
                            <Input placeholder="State" />
                          </AutoComplete>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          name="business_postal_code"
                          label="Pincode"
                          rules={[
                            { required: true, message: 'Pincode is required' },
                            { pattern: postalPattern, message: 'Enter a valid pincode' },
                          ]}
                        >
                          <Input placeholder="Enter pincode manually" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          name="business_country"
                          label="Country"
                          rules={[{ required: true, message: 'Country is required' }]}
                        >
                          <Input placeholder="Country" disabled />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="gst_number"
                          label="GST or Tax Registration Number"
                          rules={[
                            { required: true, message: 'GST or tax registration number is required' },
                            { pattern: gstPattern, message: 'Use uppercase alphanumeric GST or tax number' },
                          ]}
                        >
                          <Input prefix={<SafetyOutlined />} placeholder="24ABCDE1234F1Z5" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="documents"
                          label="Business Documents"
                          rules={[{ required: true, message: 'Add at least one business document' }]}
                        >
                          <Select mode="tags" tokenSeparators={[',']} placeholder="GST Certificate, Shop License, PAN Card" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                </div>
                <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                                    <>
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      title="Bank details are optional, but if you fill one field you must complete all payout fields."
                    />

                    <Row gutter={18}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="bank_account_number"
                          label="Bank Account Number"
                          rules={[
                            {
                              validator: (_, value) => {
                                const account = String(value ?? '').trim()
                                const ifsc = String(form.getFieldValue('bank_ifsc') ?? '').trim()
                                const bankName = String(form.getFieldValue('bank_name') ?? '').trim()
                                const hasAny = account.length > 0 || ifsc.length > 0 || bankName.length > 0

                                if (!hasAny || account.length > 0) {
                                  return Promise.resolve()
                                }

                                return Promise.reject(new Error('Complete all payout fields or leave all blank'))
                              },
                            },
                            {
                              validator: (_, value) => {
                                if (!value) {
                                  return Promise.resolve()
                                }

                                return accountPattern.test(String(value).trim())
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Bank account number must be 8 to 20 digits'))
                              },
                            },
                          ]}
                        >
                          <Input placeholder="123456789012" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="bank_ifsc"
                          label="IFSC or Routing Code"
                          rules={[
                            {
                              validator: (_, value) => {
                                const account = String(form.getFieldValue('bank_account_number') ?? '').trim()
                                const ifsc = String(value ?? '').trim()
                                const bankName = String(form.getFieldValue('bank_name') ?? '').trim()
                                const hasAny = account.length > 0 || ifsc.length > 0 || bankName.length > 0

                                if (!hasAny || ifsc.length > 0) {
                                  return Promise.resolve()
                                }

                                return Promise.reject(new Error('Complete all payout fields or leave all blank'))
                              },
                            },
                            {
                              validator: (_, value) => {
                                if (!value) {
                                  return Promise.resolve()
                                }

                                return ifscPattern.test(String(value).trim().toUpperCase())
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Enter a valid IFSC or routing code'))
                              },
                            },
                          ]}
                        >
                          <Input placeholder="HDFC0001234" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="bank_name"
                          label="Bank Name"
                          rules={[
                            {
                              validator: (_, value) => {
                                const account = String(form.getFieldValue('bank_account_number') ?? '').trim()
                                const ifsc = String(form.getFieldValue('bank_ifsc') ?? '').trim()
                                const bankName = String(value ?? '').trim()
                                const hasAny = account.length > 0 || ifsc.length > 0 || bankName.length > 0

                                if (!hasAny || bankName.length > 0) {
                                  return Promise.resolve()
                                }

                                return Promise.reject(new Error('Complete all payout fields or leave all blank'))
                              },
                            },
                          ]}
                        >
                          <Input placeholder="HDFC Bank" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                </div>
                <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
                  <>
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginBottom: 16 }}
                      title="Final step: create secure credentials and review your registration details."
                    />

                    <Row gutter={18}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="password"
                          label="Choose Your Password"
                          rules={[
                            { required: true, message: 'Password is required' },
                            {
                              pattern: strongPasswordPattern,
                              message: 'Use 8+ chars with uppercase, lowercase, number, and symbol',
                            },
                          ]}
                        >
                          <Password prefix={<LockOutlined />} placeholder="Create your seller password" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="password_confirmation"
                          label="Confirm Password"
                          dependencies={['password']}
                          rules={[
                            { required: true, message: 'Please confirm your password' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                  return Promise.resolve()
                                }
                                return Promise.reject(new Error('Passwords do not match'))
                              },
                            }),
                          ]}
                        >
                          <Password prefix={<LockOutlined />} placeholder="Confirm your password" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="terms"
                      valuePropName="checked"
                      rules={[
                        {
                          validator: (_, value) =>
                            value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms to continue')),
                        },
                      ]}
                    >
                      <Checkbox>
                        I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>
                      </Checkbox>
                    </Form.Item>
                  </>
                </div>

                {/* {currentStep === 0 ? (
                  
                ) : null} */}

                {/* {currentStep === 1 ? (

                ) : null} */}

                {/* {currentStep === 2 ? (

                ) : null} */}

                {/* {currentStep === 3 ? (
                  
                ) : null} */}

                <div className="seller-action-row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
                  <Button onClick={goPrevious} disabled={currentStep === 0}>
                    Previous stage
                  </Button>

                  {currentStep < stepTitles.length - 1 ? (
                    <Button type="primary" onClick={() => void goNext()}>
                      Continue to {stepTitles[currentStep + 1]}
                    </Button>
                  ) : (
                    <Button type="primary" htmlType="submit" style={{ minWidth: 220 }}>
                      Submit Seller Application
                    </Button>
                  )}
                </div>
              </Form>

              <div className="seller-auth-footer-text">
                Already approved? <Link to="/seller/login">Login here</Link>
              </div>
            </Card>
          </Spin>
        </div>
      </div>
    </div>
  )
}

export default SellerRegisterPage
